import classNames from 'clsx';
import React, { memo, useCallback, useRef, useState } from 'react';
import { AllDocumentsXrayTypes } from 'reactApp/modules/allDocuments/allDocuments.types';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { useGetAllDocumentsXray } from 'reactApp/sections/AllDocuments/hooks/useGetAllDocumentsXray';
import { sendDatalistThumbDwh } from 'reactApp/ui/Datalist/DataList.helpers';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getIconType } from 'reactApp/ui/FileIcon/getIconType';
import { ImageCancelable } from 'reactApp/ui/ImageCancelable/ImageCancelable';
import { withToken } from 'reactApp/utils/withToken';

import styles from '../DataListItemThumb.css';
import type { PicFileProps, PicFolderProps } from '../DataListItemThumb.types';

export const Pic = memo(function Pic(props: PicFileProps | PicFolderProps) {
    const iconType = getIconType({
        isFolder: props.isFolder,
        kind: props.isFolder ? props.kind : undefined,
        ext: props.isFolder ? undefined : props.ext.toLowerCase(),
        isVirus: props.isFolder ? false : props.isVirus,
        isAutoDelete: props.isFolder ? props.isAutoDelete : false,
    });
    const sendAllDocumentsXray = useGetAllDocumentsXray('thumb_load');
    const [isLoaded, setLoaded] = useState<boolean>(false);
    const isLoadingFolder = props.isFolder ? props.isLoading : false;

    const src = withToken(!props.isFolder ? props.thumbUrl?.src : undefined);
    const srcSet = withToken(!props.isFolder ? props.thumbUrl?.srcSet : undefined);

    const startTime = useRef(performance.now());

    const handleThumbLoad = useCallback(() => {
        setLoaded(true);

        const endTime = performance.now();

        sendDatalistThumbDwh('load', EViewMode.thumbs, props.kind, Math.round(endTime - startTime.current));
        sendAllDocumentsXray(AllDocumentsXrayTypes.onSuccess);
    }, [props?.kind]);

    const handleThumbError = useCallback(() => {
        const endTime = performance.now();

        sendDatalistThumbDwh('fail', EViewMode.thumbs, props.kind, Math.round(endTime - startTime.current), src);
        sendAllDocumentsXray(AllDocumentsXrayTypes.onError);
    }, [props?.kind, src]);

    return (
        <div className={styles.container}>
            {!isLoaded && (
                <div
                    className={classNames({
                        [styles.icon]: true,
                        [styles.loading_folder]: isLoadingFolder,
                    })}
                >
                    <FileIcon type={iconType} size="l" />
                </div>
            )}
            {!props.isFolder && src && (
                <ImageCancelable
                    className={classNames({
                        [styles.thumb]: true,
                        [styles.thumb_loaded]: isLoaded,
                        [styles.thumb_short]: props.short,
                    })}
                    draggable={false}
                    src={src}
                    srcSet={srcSet ? `${srcSet} 2x` : ''}
                    alt=""
                    onError={handleThumbError}
                    onLoad={handleThumbLoad}
                />
            )}
        </div>
    );
});
